import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Image {
  id: string;
  filename: string;
  folder: string;
  file_url: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string | null;
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
}

export interface ImageAnalysis {
  suggestedFilename: string;
  suggestedTags: string[];
  altText: string;
}

export type UploadErrorType = 
  | 'auth_error'
  | 'duplicate_error'
  | 'storage_error'
  | 'database_error'
  | 'network_error'
  | 'unknown_error';

export interface UploadError {
  type: UploadErrorType;
  message: string;
  details?: string;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  imageId?: string;
  error?: UploadError;
}

const classifyError = (error: any, context: string): UploadError => {
  const message = error?.message || String(error);
  const code = error?.code || error?.statusCode;
  
  console.error(`[Upload Error] Context: ${context}`, { error, message, code });
  
  // Network errors
  if (message.includes('fetch') || message.includes('network') || message.includes('Failed to fetch')) {
    return {
      type: 'network_error',
      message: 'Network connection failed',
      details: 'Check your internet connection and try again'
    };
  }
  
  // Auth errors
  if (message.includes('Not authenticated') || message.includes('JWT') || code === 401 || code === 403) {
    return {
      type: 'auth_error',
      message: 'Authentication required',
      details: 'Please log in again to upload images'
    };
  }
  
  // Duplicate errors
  if (message.includes('already exists') || message.includes('duplicate') || code === '23505') {
    return {
      type: 'duplicate_error',
      message: 'File already exists',
      details: message
    };
  }
  
  // File size errors
  if (message.includes('maximum allowed size') || message.includes('too large') || message.includes('file size')) {
    return {
      type: 'storage_error',
      message: 'File too large',
      details: 'The file exceeds the 10MB storage limit. Try compressing the image first.'
    };
  }
  
  // Storage errors
  if (message.includes('storage') || message.includes('bucket') || message.includes('The resource already exists')) {
    return {
      type: 'storage_error',
      message: 'Storage error',
      details: message
    };
  }
  
  // RLS/permission errors
  if (message.includes('row-level security') || message.includes('permission') || message.includes('policy')) {
    return {
      type: 'database_error',
      message: 'Permission denied',
      details: 'You do not have permission to upload. Contact an administrator.'
    };
  }
  
  // Database errors
  if (context === 'database' || message.includes('database') || message.includes('insert')) {
    return {
      type: 'database_error',
      message: 'Database error',
      details: message
    };
  }
  
  return {
    type: 'unknown_error',
    message: 'Upload failed',
    details: message
  };
};

export const useImages = (folder?: string) => {
  return useQuery({
    queryKey: ['images', folder],
    queryFn: async () => {
      let query = supabase
        .from('images')
        .select('*')
        .order('uploaded_at', { ascending: false });
      
      if (folder && folder !== 'all') {
        query = query.ilike('folder', folder);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Image[];
    },
  });
};

export const useUploadImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      file, 
      folder, 
      customFilename,
      tagIds 
    }: { 
      file: File; 
      folder: string; 
      customFilename?: string;
      tagIds?: string[];
    }): Promise<UploadResult> => {
      const timestamp = new Date().toISOString();
      const finalFilename = customFilename || file.name;
      console.log(`[Upload Start] ${timestamp} - File: ${finalFilename}, Folder: ${folder}, Size: ${(file.size / 1024).toFixed(1)}KB`);
      
      // Step 1: Check authentication
      console.log(`[Upload] Checking authentication...`);
      let user;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (authError) {
          console.error(`[Upload] Auth error:`, authError);
          return {
            success: false,
            error: classifyError(authError, 'auth')
          };
        }
        user = data.user;
        if (!user) {
          console.error(`[Upload] No user found`);
          return {
            success: false,
            error: {
              type: 'auth_error',
              message: 'Not authenticated',
              details: 'Please log in to upload images'
            }
          };
        }
        console.log(`[Upload] Authenticated as: ${user.email}`);
      } catch (error) {
        return {
          success: false,
          error: classifyError(error, 'auth')
        };
      }
      
      // Step 2: Check for duplicate filename
      console.log(`[Upload] Checking for duplicate filename...`);
      try {
        const { data: existingFiles, error: checkError } = await supabase
          .from('images')
          .select('filename')
          .eq('folder', folder)
          .ilike('filename', finalFilename)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error(`[Upload] Duplicate check error:`, checkError);
          return {
            success: false,
            error: classifyError(checkError, 'database')
          };
        }

        if (existingFiles) {
          console.error(`[Upload] Duplicate file found: ${finalFilename}`);
          return {
            success: false,
            error: {
              type: 'duplicate_error',
              message: 'File already exists',
              details: `A file named "${finalFilename}" already exists in the ${folder} folder`
            }
          };
        }
        console.log(`[Upload] No duplicate found`);
      } catch (error) {
        return {
          success: false,
          error: classifyError(error, 'database')
        };
      }
      
      // Step 3: Upload to storage (rename file if needed)
      console.log(`[Upload] Uploading to storage...`);
      const filePath = `${folder}/${finalFilename}`;
      
      // Create a new file with the custom filename if provided
      const fileToUpload = customFilename 
        ? new File([file], finalFilename, { type: file.type })
        : file;
      
      try {
        const { error: uploadError } = await supabase.storage
          .from('blog-images')
          .upload(filePath, fileToUpload, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error(`[Upload] Storage error:`, uploadError);
          return {
            success: false,
            error: classifyError(uploadError, 'storage')
          };
        }
        console.log(`[Upload] Storage upload successful`);
      } catch (error) {
        return {
          success: false,
          error: classifyError(error, 'storage')
        };
      }
      
      // Step 4: Get public URL
      console.log(`[Upload] Getting public URL...`);
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);
      console.log(`[Upload] Public URL: ${publicUrl}`);
      
      // Step 5: Create database record
      console.log(`[Upload] Creating database record...`);
      let imageId: string;
      try {
        const { data: insertedImage, error: dbError } = await supabase
          .from('images')
          .insert({
            filename: finalFilename,
            folder,
            file_url: publicUrl,
            file_size: file.size,
            uploaded_by: user.id
          })
          .select('id')
          .single();
        
        if (dbError || !insertedImage) {
          console.error(`[Upload] Database error:`, dbError);
          // Try to clean up the storage file since DB insert failed
          console.log(`[Upload] Attempting to clean up storage file after DB error...`);
          await supabase.storage.from('blog-images').remove([filePath]);
          return {
            success: false,
            error: classifyError(dbError, 'database')
          };
        }
        imageId = insertedImage.id;
        console.log(`[Upload] Database record created successfully with ID: ${imageId}`);
      } catch (error) {
        // Try to clean up the storage file
        await supabase.storage.from('blog-images').remove([filePath]);
        return {
          success: false,
          error: classifyError(error, 'database')
        };
      }
      
      // Step 6: Apply tags if provided
      if (tagIds && tagIds.length > 0) {
        console.log(`[Upload] Applying ${tagIds.length} tags...`);
        try {
          const tagInserts = tagIds.map(tagId => ({
            image_id: imageId,
            tag_id: tagId
          }));
          
          const { error: tagError } = await supabase
            .from('image_tags')
            .insert(tagInserts);
          
          if (tagError) {
            console.error(`[Upload] Tag error (non-fatal):`, tagError);
            // Don't fail the upload for tag errors
          } else {
            console.log(`[Upload] Tags applied successfully`);
          }
        } catch (error) {
          console.error(`[Upload] Tag error (non-fatal):`, error);
        }
      }
      
      console.log(`[Upload Complete] ${finalFilename} uploaded successfully`);
      return {
        success: true,
        url: publicUrl,
        imageId
      };
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['images'] });
        queryClient.invalidateQueries({ queryKey: ['image-tags'] });
      }
    },
  });
};

// Hook to analyze an image using AI
export const useAnalyzeImage = () => {
  return useMutation({
    mutationFn: async ({ 
      imageBase64, 
      currentFilename,
      availableTags 
    }: { 
      imageBase64: string; 
      currentFilename: string;
      availableTags?: Tag[];
    }): Promise<ImageAnalysis> => {
      console.log(`[Analyze] Starting AI analysis for: ${currentFilename}`);
      
      const { data, error } = await supabase.functions.invoke('analyze-image', {
        body: { 
          imageBase64, 
          currentFilename,
          availableTags 
        }
      });
      
      if (error) {
        console.error(`[Analyze] Edge function error:`, error);
        throw new Error(error.message || 'AI analysis failed');
      }
      
      if (data.error) {
        console.error(`[Analyze] Analysis error:`, data.error);
        throw new Error(data.error);
      }
      
      console.log(`[Analyze] Analysis complete:`, data);
      return data as ImageAnalysis;
    },
  });
};

// Hook to fetch all available tags
export const useTags = () => {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as Tag[];
    },
  });
};

export const useDeleteImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (imageId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Get image data first to know the file path
      const { data: image, error: fetchError } = await supabase
        .from('images')
        .select('filename, folder')
        .eq('id', imageId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Delete from storage
      const filePath = `${image.folder}/${image.filename}`;
      const { error: storageError } = await supabase.storage
        .from('blog-images')
        .remove([filePath]);
      
      if (storageError) throw storageError;
      
      // Delete from database
      const { error: dbError } = await supabase
        .from('images')
        .delete()
        .eq('id', imageId);
      
      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
};

export const useRenameImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ imageId, newFilename }: { imageId: string; newFilename: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Get current image data
      const { data: image, error: fetchError } = await supabase
        .from('images')
        .select('*')
        .eq('id', imageId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const oldPath = `${image.folder}/${image.filename}`;
      const newPath = `${image.folder}/${newFilename}`;
      
      // Copy file to new location
      const { error: copyError } = await supabase.storage
        .from('blog-images')
        .copy(oldPath, newPath);
      
      if (copyError) throw copyError;
      
      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from('blog-images')
        .remove([oldPath]);
      
      if (deleteError) throw deleteError;
      
      // Get new public URL
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(newPath);
      
      // Update database record
      const { error: updateError } = await supabase
        .from('images')
        .update({
          filename: newFilename,
          file_url: publicUrl
        })
        .eq('id', imageId);
      
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
};

// Hook to fetch images by tags
export const useImagesByTags = (tagSlugs: string[]) => {
  return useQuery({
    queryKey: ['images', 'by-tags', tagSlugs],
    queryFn: async () => {
      if (tagSlugs.length === 0) return [];
      
      const { data, error } = await supabase
        .from('images')
        .select(`
          *,
          image_tags!inner(
            tag:tags!inner(slug)
          )
        `)
        .in('image_tags.tag.slug', tagSlugs)
        .order('uploaded_at', { ascending: false });
      
      if (error) throw error;
      return data as Image[];
    },
    enabled: tagSlugs.length > 0,
  });
};

// Hook to get tags for a specific image
export const useImageTags = (imageId: string) => {
  return useQuery({
    queryKey: ['image-tags', imageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('image_tags')
        .select(`
          tag:tags(*)
        `)
        .eq('image_id', imageId);
      
      if (error) throw error;
      return data.map(item => item.tag);
    },
    enabled: !!imageId,
  });
};

// Hook to add a tag to an image
export const useAddImageTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ imageId, tagId }: { imageId: string; tagId: string }) => {
      const { error } = await supabase
        .from('image_tags')
        .insert({ image_id: imageId, tag_id: tagId });
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
      queryClient.invalidateQueries({ queryKey: ['image-tags', variables.imageId] });
      toast.success('Tag added to image');
    },
    onError: (error) => {
      console.error('Error adding tag to image:', error);
      toast.error('Failed to add tag to image');
    },
  });
};

// Hook to remove a tag from an image
export const useRemoveImageTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ imageId, tagId }: { imageId: string; tagId: string }) => {
      const { error } = await supabase
        .from('image_tags')
        .delete()
        .eq('image_id', imageId)
        .eq('tag_id', tagId);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
      queryClient.invalidateQueries({ queryKey: ['image-tags', variables.imageId] });
      toast.success('Tag removed from image');
    },
    onError: (error) => {
      console.error('Error removing tag from image:', error);
      toast.error('Failed to remove tag from image');
    },
  });
};
